import React, { useCallback, useMemo } from 'react';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { livyConfig } from 'src/lib/livyTransport';
import { LivyPanel } from './LivyPanel';
import { LivyAvatar } from './LivyAvatar';
import { useLivyChat } from './useLivyChat';
import { derivePageContext } from './pageContext';
import { LivyTheme } from './LivyTheme';
import { freshToken } from 'src/lib/livyApi';
const STARTERS = ['Explain what I am looking at', 'Why does this show $0.00?', 'What changed since last cycle?'];

/**
 * The same engine as the full page, in a drawer that knows the route it was opened from. Page vs
 * drawer is a shell difference only, which is why there is one useLivyChat and not two.
 */
function LivyDrawerBody(_ref) {
  var _localStorage$getItem;
  let open = _ref.open,
    onClose = _ref.onClose;
  const location = useLocation();
  // Not read from storage: nothing in the app writes a company id there, so this was always null
  // and the drawer quietly sent no company at all. The server derives it from the verified token,
  // which is the only trustworthy source anyway.
  const companyLabel = (_localStorage$getItem = localStorage.getItem(USER_SETTINGS_KEYS.companyName)) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : undefined;
  // freshToken, not the raw store: a streaming turn signs its own request and never passes
  // through the app's refresh interceptor.
  const getToken = useCallback(() => freshToken(), []);
  const context = useMemo(() => derivePageContext(location.pathname, location.search), [location.pathname, location.search]);
  const config = livyConfig();
  const _useLivyChat = useLivyChat({
      config: config !== null && config !== void 0 ? config : {
        endpoint: '',
        region: '',
        identityPoolId: '',
        userPoolId: ''
      },
      getToken,
      context,
      surface: 'drawer'
    }),
    messages = _useLivyChat.messages,
    streaming = _useLivyChat.streaming,
    error = _useLivyChat.error,
    send = _useLivyChat.send,
    stop = _useLivyChat.stop,
    retry = _useLivyChat.retry,
    conversationId = _useLivyChat.conversationId;
  if (!config) return null;
  return /*#__PURE__*/React.createElement(Drawer, {
    anchor: "right",
    open: open,
    onClose: onClose,
    PaperProps: {
      sx: {
        width: {
          xs: '100%',
          sm: 460
        }
      }
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    sx: {
      height: '100%',
      minHeight: 0
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    alignItems: "center",
    justifyContent: "space-between",
    sx: {
      px: '16px',
      py: '12px',
      borderBottom: 1,
      borderColor: 'divider'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "10px",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(LivyAvatar, {
    size: 34,
    thinking: streaming
  }), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6"
  }, "Livy"), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, context.entityType ? "Looking at this ".concat(context.entityType) : 'Looking at this page'))), /*#__PURE__*/React.createElement(IconButton, {
    onClick: onClose,
    "aria-label": "Close Livy"
  }, "\xD7")), /*#__PURE__*/React.createElement(LivyPanel, {
    conversationId: conversationId
    // The same surface the turn runs on. Telling the panel 'pm' while the hook sent
    // 'drawer' fetched the wrong capability groups and filed feedback under a surface
    // nobody was using.
    ,
    surface: "drawer",
    messages: messages,
    streaming: streaming,
    error: error,
    companyLabel: companyLabel,
    starters: STARTERS,
    onSend: send,
    onStop: stop,
    onRetry: retry
  })));
}

/** Ripples off inside Livy: see LivyTheme. */
export function LivyDrawer(props) {
  return /*#__PURE__*/React.createElement(LivyTheme, null, /*#__PURE__*/React.createElement(LivyDrawerBody, props));
}